import React, { useState, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import { Row, Col } from "antd";
import { connectionService } from "/app/src/services";
import Controls from "./_controls";
import NewIntegration from "./newIntegrationForm";
import { useTranslation } from "react-i18next";
import { Connection } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import Table from "/app/src/components/generic/tables/table";
import DeleteButtonCell from "/app/src/components/generic/tables/deleteButtonCell";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import { useSearchParams } from "react-router-dom";
import useQueryParams from "/app/src/hooks/useQueryParams";
import { Button } from "@heroui/react";
import { toast } from "sonner";

/**
 * Tests the connection to a bridge using the provided connection ID.
 *
 * Displays a loading toast while testing, and shows a success or error toast based on the result.
 * Handles various error response formats and ensures the testing state is updated accordingly.
 *
 * @param setIsTesting - Callback to set the testing state (true while testing, false when done).
 * @param connectionId - The identifier of the connection to test.
 * @returns A promise that resolves when the test is complete.
 */
function testBridge(setIsTesting: (status: boolean) => void, connectionId) {
  setIsTesting(true);

  const toastId = toast.loading("Testing connection...");

  return connectionService
    .testSingle(connectionId)
    .then((response) => {
      toast.dismiss(toastId);
      if ("connected" in response && response.connected) {
        toast.success("Connection successful");
      } else {
        if ("errors" in response) {
          const errorMessages = Object.values(response.errors).flat();
          toast.error(errorMessages.join(" "));
        } else if ("error" in response) {
          toast.error(response.error);
        } else if ("statusCode" in response) {
          toast.error(`Connection failed - ${response.statusCode}`);
        } else {
          toast.error("Connection failed.");
        }
      }
    })
    .catch(() => {
      toast.dismiss(toastId);
      toast.error("Connection failed.");
    })
    .finally(() => {
      setIsTesting(false);
    });
}
/**
 * Component for the Philips Hue app
 * @param app: Philips Hue app
 */
export default function Hue() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [sort, setSort] = useSortUpgrade([]);
  const [activeNewIntegration, setActiveNewIntegration] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const {
    data: connections,
    count: connectionsCount,
    isFetching,
    isPlaceholderData,
    refetch,
  } = usePaginatedData<Connection>({
    queryKey: ["connections"],
    sort,
    page,
    pageSize,
    service: connectionService,
    options: { type: "Hue Remote" },
    refetchInterval: 10000,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: connectionsCount,
    isFetching,
    page,
    pageSize,
    setPage,
    setPageSize,
  });

  const refreshTable = useCallback(() => {
    refetch();
  }, [refetch]);

  const deleteHueConnection = useCallback((id: number) => {
    return connectionService.deleteSingle(id);
  }, []);
  const handleTestClick = useCallback(
    (connectionId: number) => () => {
      testBridge(setIsTesting, connectionId);
    },
    [],
  );

  const columnHelper = createColumnHelper<Connection>();
  //columns found in the table that holds the list of connected hubs
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
      columnHelper.accessor("type", {
        id: "type",
        cell: (info) => info.getValue(),
        header: () => t("translation:access_type"),
      }),
      columnHelper.accessor("id", {
        id: "test",
        cell: (info) => (
          <Button
            size="sm"
            radius="none"
            variant="bordered"
            color="default"
            onPress={handleTestClick(info.getValue())}
            className="hover:border-primary-default hover:text-primary-default"
          >
            {isTesting
              ? t("translation:testing___")
              : t("translation:test_") + t("translation:connection")}
          </Button>
        ),
        header: "",
        meta: {
          clickable: false,
          sortable: false,
        },
      }),
      columnHelper.accessor("id", {
        id: "delete",
        cell: (info) => (
          <DeleteButtonCell
            headerText={t("translation:confirm_delete_integration")}
            id={info.getValue()}
            refresh={refreshTable}
            deleteFn={deleteHueConnection}
          />
        ),
        header: "",
        meta: {
          clickable: false,
          sortable: false,
        },
      }),
    ],
    [
      columnHelper,
      deleteHueConnection,
      handleTestClick,
      isTesting,
      refreshTable,
      t,
    ],
  );

  return (
    <div className="app epicorImport">
      <Helmet>
        <title>{t("translation:philips_hue")} - ItemPath</title>
      </Helmet>

      <Controls
        name={t("translation:philips_hue")}
        activeNewIntegration={activeNewIntegration}
        setActiveNewIntegration={setActiveNewIntegration}
      />
      <Row gutter={20}>
        <Col span={3}>
          <NewIntegration />
        </Col>
        <Col span={21}>
          <Table
            loading={isPlaceholderData && isFetching}
            rows={connections}
            tableColumns={columns}
            length={connectionsCount}
            sort={sort}
            setSort={setSort}
            enableRowSelection={false}
            paginationEnabled={{
              currentPage: page,
              pageSize,
              setPage: updatePage,
              setPageSize: updatePageSize,
            }}
            emptyText={t("translation:no_bridges_connected")}
          />
        </Col>
      </Row>
    </div>
  );
}
